import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, TextArea, FormField, FormSection } from '@tadagram/ui';
import { GripVertical, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Campaign, CreateCampaignRequest, CampaignWorkflowEntry } from '../types/campaign';
import { apiClient } from '../api/client';

interface WorkflowOption {
  _id: string;
  name: string;
  status: string;
}

interface CampaignFormProps {
  campaign?: Campaign;
  onSave: (data: CreateCampaignRequest) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
}

export const CampaignForm: React.FC<CampaignFormProps> = ({
  campaign,
  onSave,
  onCancel,
  loading,
  setLoading,
  error,
  setError,
}) => {
  const { t } = useTranslation('campaign');

  const schedule = campaign?.schedule;
  const initialWindow = schedule?.time_windows?.[0] || schedule?.active_hours || { start: '09:00', end: '09:00' };

  const [name, setName] = useState(campaign?.name || '');
  const [description, setDescription] = useState(campaign?.description || '');
  const [startDate, setStartDate] = useState(campaign?.start_date?.split('T')[0] || '');
  const [endDate, setEndDate] = useState(campaign?.end_date?.split('T')[0] || '');
  const [runsPerDay, setRunsPerDay] = useState<number>(schedule?.base_frequency || 1);
  const [windowStart, setWindowStart] = useState<string>(initialWindow.start || '09:00');
  const [windowEnd, setWindowEnd] = useState<string>(initialWindow.end || initialWindow.start || '09:00');

  const [workflowEntries, setWorkflowEntries] = useState<CampaignWorkflowEntry[]>(
    campaign?.workflow_order
      ? [...campaign.workflow_order].sort((a, b) => a.order_index - b.order_index)
      : []
  );
  const [allWorkflows, setAllWorkflows] = useState<WorkflowOption[]>([]);
  const [wfLoading, setWfLoading] = useState(false);

  useEffect(() => {
    setWfLoading(true);
    apiClient.getWorkflowList()
      .then(list => setAllWorkflows(list))
      .catch(err => console.error('Failed to load workflows:', err))
      .finally(() => setWfLoading(false));
  }, []);

  // Ids already in the sequence
  const usedIds = new Set(workflowEntries.map(e => e.workflow_id));

  const addWorkflow = (wfId: string) => {
    const wf = allWorkflows.find(w => w._id === wfId);
    if (!wf) return;
    setWorkflowEntries(prev => [
      ...prev,
      { workflow_id: wfId, order_index: prev.length, delay_minutes: 0, workflow_name: wf.name },
    ]);
  };

  const removeWorkflow = (idx: number) => {
    setWorkflowEntries(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((e, i) => ({ ...e, order_index: i }));
    });
  };

  const moveWorkflow = (idx: number, dir: -1 | 1) => {
    setWorkflowEntries(prev => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((e, i) => ({ ...e, order_index: i }));
    });
  };

  const setDelay = (idx: number, minutes: number) => {
    setWorkflowEntries(prev =>
      prev.map((e, i) => i === idx ? { ...e, delay_minutes: minutes } : e)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError(t('nameRequired')); return; }
    if (!startDate)   { setError(t('startDateRequired')); return; }
    setLoading(true);
    setError(null);
    try {
      const payload: CreateCampaignRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        start_date: startDate,
        end_date: endDate || undefined,
        schedule: {
          base_frequency: Math.max(1, Math.min(24, runsPerDay || 1)),
          weekdays: [0, 1, 2, 3, 4, 5, 6],
          active_hours: { start: windowStart, end: windowEnd },
          time_windows: [{ start: windowStart, end: windowEnd }],
          timezone: 'Asia/Ho_Chi_Minh',
        },
        objectives: { primary_goal: 'engagement' },
        workflow_order: workflowEntries.map((e, i) => ({
          workflow_id: e.workflow_id,
          order_index: i,
          delay_minutes: e.delay_minutes ?? 0,
          workflow_name: e.workflow_name,
        })),
      };
      await onSave(payload);
    } catch (err: any) {
      setError(err.message || t('saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const availableWorkflows = allWorkflows.filter(w => !usedIds.has(w._id));

  return (
    <form id="campaign-form" onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Section 1: Basic info */}
      <FormSection title={t('sectionBasic')}>
        <FormField label={t('campaignName')} required>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('campaignNamePlaceholder')}
            required
          />
        </FormField>

        <FormField label={t('description')}>
          <TextArea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t('descriptionPlaceholder')}
            rows={2}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('startDate')} required>
            <Input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              required
            />
          </FormField>
          <FormField label={t('endDate')}>
            <Input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </FormField>
        </div>

        <FormField label={t('runsPerDay')}>
          <Input
            type="number"
            min={1}
            max={24}
            value={runsPerDay}
            onChange={e => setRunsPerDay(parseInt(e.target.value, 10) || 1)}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('windowStart')}>
            <Input
              type="time"
              value={windowStart}
              onChange={e => setWindowStart(e.target.value || '09:00')}
            />
          </FormField>
          <FormField label={t('windowEnd')}>
            <Input
              type="time"
              value={windowEnd}
              onChange={e => setWindowEnd(e.target.value || windowStart || '09:00')}
            />
          </FormField>
        </div>

        <div className="text-xs text-tg-hint">
          {t('windowHint')}
        </div>
      </FormSection>

      {/* Section 2: Workflow sequence */}
      <FormSection title={t('sectionWorkflows')}>
        <p className="text-sm text-tg-hint mb-3">{t('workflowsHint')}</p>

        {workflowEntries.length > 0 && (
          <div className="space-y-2 mb-4">
            {workflowEntries.map((entry, idx) => (
              <div
                key={entry.workflow_id}
                className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
              >
                <GripVertical size={16} className="text-gray-400 shrink-0" />

                <span className="text-xs font-bold text-tg-hint w-5 shrink-0">{idx + 1}</span>

                <span className="flex-1 text-sm font-medium text-tg-text truncate">
                  {entry.workflow_name || entry.workflow_id}
                </span>

                {idx > 0 && (
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-xs text-tg-hint">+</span>
                    <input
                      type="number"
                      min={0}
                      max={24 * 60 * 7}
                      value={entry.delay_minutes ?? 0}
                      onChange={e => setDelay(idx, parseInt(e.target.value, 10) || 0)}
                      className="w-14 text-xs border border-gray-300 rounded px-1.5 py-1 text-center"
                    />
                    <span className="text-xs text-tg-hint">{t('minutes')}</span>
                  </div>
                )}

                <div className="flex flex-col shrink-0">
                  <button
                    type="button"
                    onClick={() => moveWorkflow(idx, -1)}
                    disabled={idx === 0}
                    className="p-0.5 text-gray-400 hover:text-tg-primary disabled:opacity-30"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveWorkflow(idx, 1)}
                    disabled={idx === workflowEntries.length - 1}
                    className="p-0.5 text-gray-400 hover:text-tg-primary disabled:opacity-30"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => removeWorkflow(idx)}
                  className="p-1 text-red-400 hover:text-red-600 shrink-0"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}

        {wfLoading ? (
          <p className="text-sm text-tg-hint">{t('loadingWorkflows')}</p>
        ) : availableWorkflows.length > 0 ? (
          <div>
            <p className="text-xs text-tg-hint mb-2">{t('addWorkflow')}</p>
            <div className="flex flex-wrap gap-2">
              {availableWorkflows.map(wf => (
                <button
                  key={wf._id}
                  type="button"
                  onClick={() => addWorkflow(wf._id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-tg-primary/40 text-tg-primary rounded-lg hover:bg-tg-primary/5 transition-colors"
                >
                  <Plus size={14} />
                  {wf.name}
                </button>
              ))}
            </div>
          </div>
        ) : allWorkflows.length > 0 ? (
          <p className="text-sm text-tg-hint italic">{t('allWorkflowsAdded')}</p>
        ) : (
          <p className="text-sm text-tg-hint italic">{t('noWorkflowsAvailable')}</p>
        )}
      </FormSection>
    </form>
  );
};
